import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, Button, Tag, Modal, Form, Input, Checkbox, Space, App, InputNumber, Switch, Tooltip, Popconfirm, theme } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, CheckCircleTwoTone, MinusCircleTwoTone, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../api/client';
import { useAuth } from '../store/AuthContext';
import { formatDateTime, formatDateTimeCompact, parseApiDate } from '../utils/datetime';

const { Title, Text, Paragraph } = Typography;

interface APIKey {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  sourceKey?: string;
  rateLimitPerMinute?: number;
  enabled: boolean;
  createdAt: string;
  lastUsedAt?: string;
}

const allPermissions = [
  { value: 'servers:read', label: 'Servers Read' },
  { value: 'servers:write', label: 'Servers Write' },
  { value: 'health:read', label: 'Health Read' },
  { value: 'health:trigger', label: 'Health Trigger' },
  { value: 'integration:read', label: 'Integration Read' },
  { value: 'integration:write', label: 'Integration Write' },
];

const APIKeysPage: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { user } = useAuth();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<APIKey | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const isAdmin = user?.role === 'admin';

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api-keys');
      // The list handler returns { items: [...] } — older code read .keys
      // which silently produced an empty list. Both shapes handled for safety.
      setKeys(data.data.items || data.data.keys || []);
    } catch {
      message.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async (values: any) => {
    try {
      const payload: any = {
        name: values.name,
        permissions: values.permissions,
      };
      if (values.sourceKey && values.sourceKey.trim()) payload.sourceKey = values.sourceKey.trim();
      if (values.rateLimitPerMinute && values.rateLimitPerMinute > 0) payload.rateLimitPerMinute = values.rateLimitPerMinute;

      const { data } = await apiClient.post('/api-keys', payload);
      setNewKey(data.data.key);
      setCreateOpen(false);
      createForm.resetFields();
      fetchKeys();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create API key');
    }
  };

  const openEdit = (record: APIKey) => {
    editForm.setFieldsValue({
      name: record.name,
      permissions: record.permissions || [],
      sourceKey: record.sourceKey || '',
      rateLimitPerMinute: record.rateLimitPerMinute || 0,
      enabled: record.enabled,
    });
    setEditing(record);
  };

  const saveEdit = async (values: any) => {
    if (!editing) return;
    try {
      const payload: any = {
        name: values.name,
        permissions: values.permissions,
        rateLimitPerMinute: typeof values.rateLimitPerMinute === 'number' ? values.rateLimitPerMinute : 0,
        enabled: values.enabled,
      };
      if (isAdmin) {
        // Only admins can change the sourceKey filter.
        payload.sourceKey = (values.sourceKey || '').trim();
      }
      await apiClient.put(`/api-keys/${editing.id}`, payload);
      message.success('API key updated');
      setEditing(null);
      editForm.resetFields();
      fetchKeys();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update API key');
    }
  };

  const deleteKey = async (id: string) => {
    try {
      await apiClient.delete(`/api-keys/${id}`);
      message.success('API key revoked');
      fetchKeys();
    } catch {
      message.error('Failed to revoke API key');
    }
  };

  const regenerateKey = async (id: string) => {
    try {
      // Backend returns the unmasked key exactly once on rotate — same
      // contract as Create. Reuse the reveal modal so the admin has a single
      // place to copy from.
      const { data } = await apiClient.post(`/api-keys/${id}/regenerate`);
      setEditing(null);
      editForm.resetFields();
      setNewKey(data.data.key);
      fetchKeys();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to regenerate key');
    }
  };

  const columns = [
    {
      title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true,
      render: (v: string) => <Text ellipsis={{ tooltip: v }} style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      // Two permission tags per row used to wrap and double the row height.
      // Now the row stays one line: first two tags inline, the rest folded into
      // a "+N" tag whose tooltip lists every grant — nothing is lost.
      title: 'Permissions', key: 'permissions', width: 230,
      render: (_: any, r: APIKey) => {
        const perms = r.permissions || [];
        const head = perms.slice(0, 2);
        const rest = perms.slice(2);
        return (
          <Tooltip title={perms.join(', ') || 'no permissions'}>
            <Space size={2} wrap={false}>
              {head.map((p) => (
                <Tag key={p} style={{ margin: 0, fontSize: 12, lineHeight: '18px' }}>{p}</Tag>
              ))}
              {rest.length > 0 && (
                <Tag style={{ margin: 0, fontSize: 12, lineHeight: '18px' }}>+{rest.length}</Tag>
              )}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Source', key: 'sourceKey', width: 120,
      render: (_: any, r: APIKey) => r.sourceKey
        ? <Tag color="geekblue" style={{ margin: 0, fontSize: 12 }}>{r.sourceKey}</Tag>
        : <Text type="secondary" style={{ fontSize: 12 }}>unrestricted</Text>,
    },
    {
      title: 'Rate', key: 'rateLimitPerMinute', width: 84, align: 'right' as const,
      render: (_: any, r: APIKey) => r.rateLimitPerMinute
        ? <Text style={{ fontSize: 13 }}>{r.rateLimitPerMinute}/min</Text>
        : <Tooltip title="Unlimited"><Text type="secondary">—</Text></Tooltip>,
    },
    {
      title: 'Enabled', dataIndex: 'enabled', key: 'enabled', width: 76, align: 'center' as const,
      render: (v: boolean) => v
        ? <Tooltip title="Enabled"><CheckCircleTwoTone twoToneColor={token.colorSuccess} /></Tooltip>
        : <Tooltip title="Disabled — requests rejected"><MinusCircleTwoTone twoToneColor={token.colorError} /></Tooltip>,
    },
    {
      title: 'Last used', dataIndex: 'lastUsedAt', key: 'lastUsedAt', width: 120,
      render: (v: string) => parseApiDate(v)
        ? <Tooltip title={formatDateTime(v)}><Text type="secondary" style={{ fontSize: 12 }}>{formatDateTimeCompact(v)}</Text></Tooltip>
        : <Text type="secondary" style={{ fontSize: 12 }}>never</Text>,
    },
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 120,
      render: (v: string) => (
        <Tooltip title={formatDateTime(v)}>
          <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTimeCompact(v)}</Text>
        </Tooltip>
      ),
    },
    {
      // Icon-only actions: the labels cost 90px per row and say what the icons
      // already say; the words survive as tooltips.
      title: '', key: 'actions', width: 72, align: 'right' as const,
      render: (_: any, r: APIKey) => (
        <Space size={4}>
          <Tooltip title="Edit"><Button size="small" type="text" aria-label={`Edit ${r.name}`} icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          {/* Revoking breaks the integrator immediately and cannot be undone —
              one stray click on a 24px icon must not do that. */}
          <Popconfirm
            title="Revoke this key?"
            description="Requests using it will be rejected immediately."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteKey(r.id)}
          >
            <Tooltip title="Revoke"><Button size="small" type="text" danger aria-label={`Revoke ${r.name}`} icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
        <Space size={8} align="baseline">
          <Title level={4} style={{ margin: 0 }}>API Keys</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{keys.length} total</Text>
        </Space>
        {isAdmin && (
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create API Key
          </Button>
        )}
      </Space>

      {/* Explainer kept, but at 12px and capped to a readable measure so it
          costs two short lines instead of a quarter of the viewport. */}
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10, maxWidth: 820 }}>
        API keys authenticate external systems. A key with a <Text code style={{ fontSize: 14 }}>Source</Text> filter only sees and mutates
        servers whose <Text code style={{ fontSize: 14 }}>sourceKey</Text> matches — useful when you grant access to one integrator without exposing the rest.
        <Text code style={{ fontSize: 14 }}>Rate</Text> is per-minute; 0 (—) means unlimited.
      </Paragraph>

      <Table
        dataSource={keys}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
      />

      {/* Create */}
      <Modal
        title="Create API Key"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={createForm} size="small" layout="vertical" onFinish={createKey} initialValues={{ rateLimitPerMinute: 10 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Sysboard integration" />
          </Form.Item>
          <Form.Item name="permissions" label="Permissions" rules={[{ required: true, message: 'Pick at least one permission' }]}>
            <Checkbox.Group options={allPermissions} />
          </Form.Item>
          <Form.Item
            name="sourceKey"
            label="Source filter (optional)"
            tooltip="Restrict this key to servers that have the given sourceKey. Leave blank for unrestricted access."
          >
            <Input placeholder="sysboard" />
          </Form.Item>
          <Form.Item
            name="rateLimitPerMinute"
            label="Rate limit (requests / minute)"
            tooltip="0 = unlimited. Enforced in Redis; bucket resets each minute."
          >
            <InputNumber min={0} max={100000} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Create</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit */}
      <Modal
        title="Edit API Key"
        open={!!editing}
        onCancel={() => { setEditing(null); editForm.resetFields(); }}
        footer={null}
        destroyOnHidden
      >
        <Form form={editForm} size="small" layout="vertical" onFinish={saveEdit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="permissions" label="Permissions" rules={[{ required: true }]}>
            <Checkbox.Group options={allPermissions} />
          </Form.Item>
          <Form.Item
            name="sourceKey"
            label="Source filter"
            tooltip={isAdmin ? 'Empty = unrestricted' : 'Only admins can change this filter'}
          >
            <Input disabled={!isAdmin} placeholder="unrestricted" />
          </Form.Item>
          <Form.Item name="rateLimitPerMinute" label="Rate limit (requests / minute)">
            <InputNumber min={0} max={100000} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Save</Button>
              {editing && (
                <Popconfirm
                  title="Regenerate key?"
                  description="The current key will stop working immediately. You'll see the new key once — copy it safely."
                  okText="Regenerate"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => regenerateKey(editing.id)}
                >
                  <Button icon={<ReloadOutlined />}>Regenerate key</Button>
                </Popconfirm>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* One-shot key reveal — covers both initial create and regenerate.
          After this modal closes the key is gone for good. */}
      <Modal
        title="API Key — copy it now"
        open={!!newKey}
        onCancel={() => setNewKey(null)}
        footer={<Button type="primary" onClick={() => setNewKey(null)}>Done</Button>}
      >
        <Paragraph>
          This is the only time you'll see the key. Store it somewhere safe —
          if you lose it, use <Text strong>Regenerate</Text> in the edit dialog to mint a new one.
        </Paragraph>
        <Space>
          <Text code copyable style={{ fontSize: 13 }}>{newKey}</Text>
        </Space>
      </Modal>
    </>
  );
};

export default APIKeysPage;
